import React, { useMemo } from 'react';
import { Briefcase } from 'lucide-react';
import { useMarket } from '../context/MarketContext';
import type { Position } from '../types';
import { toEAT } from '../utils/time';
import { INSTRUMENTS } from '../constants/instruments';

// Create mapping from symbol label to secId
const SECID_BY_LABEL = INSTRUMENTS.reduce<Record<string, string>>((m, { label, secId }) => {
  m[label.toUpperCase()] = secId.toUpperCase();
  return m;
}, {});

// Helper to get secId from symbol (symbol might be label or secId)
function getSecId(symbol: string): string {
  const upperSymbol = symbol.toUpperCase();
  // If symbol is already a secId (not in SECID_BY_LABEL), return as-is
  // Otherwise, look up the secId from the label
  return SECID_BY_LABEL[upperSymbol] || upperSymbol;
}

export default function PositionsPage() {
  const { positions, vwapDataBySymbol } = useMarket();
  
  // Calculate PnL on frontend using VWAP prices
  const { calculatedPositions, totals } = useMemo(() => {
    const calculated: Array<Position & { vwapPrice?: number }> = [];
    let totalU = 0;
    let totalR = 0;
    let totalExposure = 0;
    
    positions.forEach(pos => {
      // Get secId for this position (VWAP data is stored by secId)
      const secId = getSecId(pos.symbol);
      
      // Get latest VWAP price for this secId
      const vwapData = vwapDataBySymbol.get(secId);
      const vwapPrice = vwapData && vwapData.length > 0 
        ? vwapData[vwapData.length - 1].vwap 
        : undefined;
      
      // Calculate unrealized PnL using VWAP price
      // unrealized = (currentPrice - avgPrice) * quantity
      const unrealizedPnL = vwapPrice && pos.avgPrice 
        ? (vwapPrice - pos.avgPrice) * pos.quantity
        : pos.unrealizedPnL; // Fallback to backend value if VWAP not available
      
      // Use realized PnL from position (from backend)
      const realizedPnL = pos.realizedPnL;
      
      // Calculate total PnL
      const totalPnL = unrealizedPnL + realizedPnL;
      
      // Calculate exposure
      const exposure = vwapPrice ? vwapPrice * pos.quantity : (pos.exposure ?? 0);
      
      calculated.push({
        ...pos,
        currentPrice: vwapPrice,
        unrealizedPnL,
        totalPnL,
        exposure
      });
      
      // Sum totals
      totalU += unrealizedPnL;
      totalR += realizedPnL;
      totalExposure += exposure;
    });
    
    return {
      calculatedPositions: calculated,
      totals: {
        totalUnrealizedPnL: totalU,
        totalRealizedPnL: totalR,
        totalPnL: totalU + totalR,
        totalExposure
      }
    };
  }, [positions, vwapDataBySymbol]);
  
  const totalU = totals.totalUnrealizedPnL;
  const totalR = totals.totalRealizedPnL;
  const totalPnL = totals.totalPnL;
  const totalExposure = totals.totalExposure;

  const card = { background:'#1e293b', padding:16, borderRadius:8, border:'1px solid #334155' };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      <div style={card}>
        <Section title="Positions" icon={<Briefcase size={16} color="#94a3b8"/>}/>
        <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
          <div style={{ ...card, flex:1, minWidth:200 }}>
            <Small muted="Unrealized P&L"/>
            <Big color={totalU>=0 ? '#10b981' : '#ef4444'}>{totalU.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</Big>
          </div>
          <div style={{ ...card, flex:1, minWidth:200 }}>
            <Small muted="Realized P&L"/>
            <Big color={totalR>=0 ? '#10b981' : '#ef4444'}>{totalR.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</Big>
          </div>
          <div style={{ ...card, flex:1, minWidth:200 }}>
            <Small muted="Total P&L"/>
            <Big color={totalPnL>=0 ? '#10b981' : '#ef4444'}>{totalPnL.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</Big>
          </div>
          <div style={{ ...card, flex:1, minWidth:200 }}>
            <Small muted="Total Exposure"/>
            <Big>{totalExposure.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</Big>
          </div>
        </div>
      </div>

      <div style={card}>
        <HeaderRow/>
        <div style={{ maxHeight:420, overflow:'auto' }}>
          {calculatedPositions.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
              No positions yet.
            </div>
          ) : (
            calculatedPositions.map((p,i)=>(<Row key={i} p={p}/>))
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon }:{ title:string; icon?:React.ReactNode }) {
  return <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
    {icon}{<h3 style={{ margin:0, fontSize:16, fontWeight:600 }}>{title}</h3>}
  </div>;
}
const Small = ({ muted }:{ muted:string }) => <div style={{ color:'#94a3b8', fontSize:12 }}>{muted}</div>;
const Big = ({ children, color }:{ children:React.ReactNode; color?:string }) =>
  <div style={{ fontSize:20, fontWeight:700, color }}>{children}</div>;

function HeaderRow() {
  return (
    <div style={{
      display:'grid', 
      gridTemplateColumns:'100px 1fr 1fr 1fr 1fr 1fr 1fr 140px', 
      gap:8,
      padding:'8px 0', 
      color:'#94a3b8', 
      fontSize:12, 
      borderBottom:'1px solid #334155'
    }}>
      <div>Symbol</div>
      <div style={{ textAlign:'right' }}>Qty</div>
      <div style={{ textAlign:'right' }}>Avg Price</div>
      <div style={{ textAlign:'right' }}>Current Price</div>
      <div style={{ textAlign:'right' }}>Unrealized</div>
      <div style={{ textAlign:'right' }}>Realized</div>
      <div style={{ textAlign:'right' }}>Total P&L</div>
      <div>Last Update</div>
    </div>
  );
}

function Row({ p }:{ p:Position }) {
  const totalPnL = p.totalPnL ?? (p.unrealizedPnL + p.realizedPnL);
  const exposure = p.exposure ?? (p.currentPrice ? p.currentPrice * p.quantity : 0);
  
  function formatTimestamp(time?: string): string {
    if (!time) return '—';
    try {
      const date = new Date(time);
      if (!isNaN(date.getTime())) {
        return toEAT(date);
      }
      return time;
    } catch {
      return time;
    }
  }
  
  return (
    <div style={{
      display:'grid', 
      gridTemplateColumns:'100px 1fr 1fr 1fr 1fr 1fr 1fr 140px', 
      gap:8,
      padding:'10px 0', 
      borderBottom:'1px solid #334155'
    }}>
      <div style={{ fontWeight:600 }}>{p.symbol}</div>
      <div style={{ textAlign:'right', color: p.quantity >= 0 ? '#10b981' : '#ef4444', fontWeight:500 }}>
        {p.quantity.toLocaleString()}
      </div>
      <div style={{ textAlign:'right' }}>{p.avgPrice.toFixed(2)}</div>
      <div style={{ textAlign:'right', color: p.currentPrice ? '#e2e8f0' : '#94a3b8' }}>
        {p.currentPrice ? p.currentPrice.toFixed(2) : '—'}
      </div>
      <div style={{ 
        textAlign:'right', 
        color: p.unrealizedPnL>=0 ? '#10b981':'#ef4444',
        fontWeight: 500
      }}>
        {p.unrealizedPnL.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
      </div>
      <div style={{ 
        textAlign:'right', 
        color: p.realizedPnL>=0 ? '#10b981':'#ef4444',
        fontWeight: 500
      }}>
        {p.realizedPnL.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
      </div>
      <div style={{ 
        textAlign:'right', 
        color: totalPnL>=0 ? '#10b981':'#ef4444',
        fontWeight: 600
      }}>
        {totalPnL.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
      </div>
      <div style={{ fontSize:11, color:'#94a3b8' }}>
        {formatTimestamp(p.lastUpdate)}
      </div>
    </div>
  );
}
